import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Dialog, Field, Input, Switch, toast } from "@dl/web-core-js/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage } from "../../api/manufacturing";
import type { PipelineLotDTO } from "../../api/pipeline";
import type { LazyProductDTO } from "../../api/product";
import { ProductionOrderClient, type LotConversionRequest, type LotConversionResult } from "../../api/production";

interface Row {
  lot: PipelineLotDTO;
  quantity: string;
  due: string;
}

/** The Monday after today (a week ahead when today is a Monday), as yyyy-mm-dd in local time. */
export function nextMonday(from: Date = new Date()): string {
  const day = from.getDay(); // 0 Sunday … 6 Saturday
  const ahead = ((8 - day) % 7) || 7;
  const monday = new Date(from.getFullYear(), from.getMonth(), from.getDate() + ahead);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${monday.getFullYear()}-${pad(monday.getMonth() + 1)}-${pad(monday.getDate())}`;
}

/**
 * Turns the selected pipeline lots into production orders, their assembly
 * orders planned with them.
 *
 * One line per lot: the quantity starts at the lot's planned size and the due
 * date at next Monday, both editable. As the lines change, the server checks
 * each lot's Laval materials against what is available, lot after lot in this
 * order (the first takes stock before the second sees it), and each line says
 * whether it can be made or what it lacks. Creating makes the lots that can; the
 * short ones stay in the pipeline. Until the go-live count, a ROOT user may make
 * them anyway, with a reason kept on each order.
 */
export function CreateOrdersDialog({
  lots,
  products,
  onClose,
}: {
  lots: PipelineLotDTO[];
  products: Map<number, LazyProductDTO>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orders = useMemo(() => new ProductionOrderClient(http), [http]);
  const [rows, setRows] = useState<Row[]>(() =>
    lots.map((lot) => ({ lot, quantity: String(lot.planning_data?.lot_size_end ?? ""), due: nextMonday() })),
  );
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const valid = rows.every((row) => Number(row.quantity) > 0);

  const request = (extra: Partial<LotConversionRequest> = {}): LotConversionRequest => ({
    lots: rows.map((row) => ({
      production_pipeline_lot_id: row.lot.id,
      quantity: Number(row.quantity),
      due_date: row.due || null,
    })),
    ...extra,
  });

  // What converting would do, per lot, for the lines as they are now.
  const check = useQuery({
    queryKey: ["production", "lot-check", rows.map((row) => [row.lot.id, row.quantity])],
    queryFn: () => orders.checkLots(request()),
    enabled: valid,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const byLot = new Map((check.data?.lots ?? []).map((lot) => [lot.production_pipeline_lot_id, lot]));
  const short = (check.data?.lots ?? []).filter((lot) => lot.shortages.length > 0).length;
  const overriding = override && short > 0;
  const count = overriding ? rows.length : rows.length - short;

  const create = useMutation({
    mutationFn: () => orders.fromLots(request(overriding ? { override_shortages: true, override_reason: reason.trim() } : {})),
    onSuccess: async (result: LotConversionResult) => {
      await queryClient.invalidateQueries({ queryKey: ["pipeline"] });
      await queryClient.invalidateQueries({ queryKey: ["production"] });
      onClose();
      const left = result.lots.filter((lot) => !lot.converted).length;
      toast.success(
        left === 0
          ? t("pipeline.orders.created", "{{count}} production orders created", { count: result.orders.length })
          : t("pipeline.orders.created-some", "{{count}} production orders created; {{left}} lots short of materials stay in the pipeline", {
              count: result.orders.length,
              left,
            }),
      );
      const [only] = result.orders;
      if (result.orders.length === 1 && only) {
        void navigate({ to: "/production/orders/$orderId", params: { orderId: String(only.id) } });
      }
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const set = (index: number, patch: Partial<Row>) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...patch } : row)));

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={t("pipeline.orders.title", "Create production orders")}
      description={t(
        "pipeline.orders.description.v2",
        "Each lot becomes a production order for its product's semi-finished set, with its assembly orders planned. Its Laval materials are reserved; lots take stock in this order, and a lot short of anything stays in the pipeline.",
      )}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("dialog.cancel", "Cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={!valid || count === 0 || check.isFetching || (overriding && reason.trim() === "")}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            {t("pipeline.orders.create", "Create {{count}} orders", { count })}
          </Button>
        </>
      }
    >
      <table className="w-full text-[13px]">
        <thead>
          <tr className="text-left text-[11px] text-ink-muted">
            <th className="pb-2 font-medium">{t("column.sku", "SKU")}</th>
            <th className="pb-2 font-medium">{t("column.mo.quantity", "Quantity")}</th>
            <th className="pb-2 font-medium">{t("column.mo.due", "Due")}</th>
            <th className="pb-2 font-medium">{t("pipeline.orders.materials", "Laval materials")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const product = products.get(row.lot.product_id);
            const lot = byLot.get(row.lot.id);
            return (
              <tr key={row.lot.id} className="border-t border-line align-top">
                <td className="py-2 pr-3">
                  <span className="font-medium">{product?.metadata?.sku ?? row.lot.product_id}</span>
                  <span className="ml-2 text-[11px] text-ink-faint">{product?.description_en?.product_name ?? ""}</span>
                </td>
                <td className="py-2 pr-3">
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    className="w-28"
                    aria-label={t("column.mo.quantity", "Quantity")}
                    value={row.quantity}
                    invalid={!(Number(row.quantity) > 0)}
                    onChange={(event) => set(index, { quantity: event.target.value })}
                  />
                </td>
                <td className="py-2 pr-3">
                  <Input
                    type="date"
                    aria-label={t("column.mo.due", "Due")}
                    value={row.due}
                    onChange={(event) => set(index, { due: event.target.value })}
                  />
                </td>
                <td className="py-2">
                  {!lot ? (
                    <span className="text-[11px] text-ink-faint">
                      {check.isFetching ? t("pipeline.orders.checking", "Checking stock…") : ""}
                    </span>
                  ) : lot.shortages.length === 0 ? (
                    <Badge variant="success" size="sm">
                      {t("pipeline.orders.ready", "In stock")}
                    </Badge>
                  ) : (
                    <details>
                      <summary className="cursor-pointer">
                        <Badge variant="error" size="sm">
                          {t("pipeline.orders.short", "Short of {{count}} materials", { count: lot.shortages.length })}
                        </Badge>
                      </summary>
                      <table className="mt-1 text-[11px]">
                        <thead>
                          <tr className="text-left text-ink-muted">
                            <th className="pr-2 font-medium">{t("column.part", "Part")}</th>
                            <th className="pr-2 text-right font-medium">{t("pipeline.orders.needed", "Needed")}</th>
                            <th className="text-right font-medium">{t("pipeline.orders.available", "Available")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lot.shortages.map((shortage) => (
                            <tr key={`${shortage.part_id}-${shortage.warehouse_code ?? ""}`}>
                              <td className="pr-2" title={shortage.part_name ?? undefined}>
                                {shortage.part_number}
                                {shortage.warehouse_code && <span className="ml-1 text-ink-faint">{shortage.warehouse_code}</span>}
                              </td>
                              <td className="pr-2 text-right tabular-nums">{formatNumber(shortage.needed)}</td>
                              <td className="text-right tabular-nums text-warn">{formatNumber(shortage.available)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {short > 0 && (
        <div className="mt-4 flex flex-col gap-3 border-t border-line pt-3">
          <p className="text-[12px] text-ink-muted">
            {t("pipeline.orders.short-note", "{{count}} lots are short of Laval materials: they stay in the pipeline until the stock is there.", {
              count: short,
            })}
          </p>
          {check.data?.override_allowed && (
            <>
              <Switch
                checked={override}
                onChange={setOverride}
                label={t("pipeline.orders.override", "Make the short lots anyway (ROOT, until the go-live count)")}
              />
              {override && (
                <Field label={t("pipeline.orders.override-reason", "Why")} htmlFor="override-reason">
                  <Input
                    id="override-reason"
                    value={reason}
                    invalid={reason.trim() === ""}
                    onChange={(event) => setReason(event.target.value)}
                  />
                </Field>
              )}
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}
